<template>
  <div>
    <Loading :active.sync="loading" :isFullPage="false"></Loading>

    <!-- List view -->
    <Card v-if="!showForm" class="p-no-shadow-app">
      <template #title>
        <div class="p-d-flex p-jc-between p-ai-center">
          <span>Recolhas</span>
          <div>
            <Button
              label="Nova Recolha"
              icon="pi pi-plus"
              class="p-button-sm p-button-success p-mr-2"
              :disabled="!canEdit"
              @click="openNewForm"
            />
            <Button
              icon="pi pi-refresh"
              class="p-button-sm p-button-secondary"
              @click="loadData"
            />
          </div>
        </div>
      </template>
      <template #content>
        <DataTable
          :value="pickups"
          responsiveLayout="scroll"
          class="p-datatable-sm"
          :rows="10"
          :paginator="pickups.length > 10"
          :expandedRows.sync="expandedRows"
          @row-expand="onRowExpand"
          @row-collapse="onRowCollapse"
        >
          <Column :expander="true" style="width: 3rem" />
          <Column field="pickupDate" header="Data" />
          <Column field="transportadora" header="Transportadora" />
          <Column field="nGuia" header="Nº Guia" />
          <Column field="estado" header="Estado">
            <template #body="slotProps">
              <span :class="estadoBadgeClass(slotProps.data)" v-if="!slotProps.data.nacex">
                {{ slotProps.data.estado }}
              </span>
              <span v-else :class="'status-badge status-' + getStatusClass(slotProps.data.nacex.status)">
                {{ slotProps.data.nacex.status }}
              </span>
              <span v-if="slotProps.data.nacex" class="p-ml-2 nacex-badge">NACEX</span>
            </template>
          </Column>
          <Column field="scheduledByName" header="Marcado por" />
          <Column header="Ações" style="width: 200px">
            <template #body="slotProps">
              <Button
                icon="pi pi-pencil"
                class="p-button-sm p-button-secondary p-mr-1"
                @click="openEditForm(slotProps.data)"
                v-tooltip.top="'Editar'"
              />
              <Button
                v-if="slotProps.data.nacex && slotProps.data.nacex.trackingUrl"
                icon="pi pi-external-link"
                class="p-button-sm p-button-info p-mr-1"
                @click="openTracking(slotProps.data.nacex.trackingUrl)"
                v-tooltip.top="'Rastrear na NACEX'"
              />
              <Button
                v-if="slotProps.data.nacex"
                icon="pi pi-sync"
                class="p-button-sm p-button-info p-mr-1"
                @click="syncNacexStatus(slotProps.data)"
                v-tooltip.top="'Sincronizar NACEX'"
              />
              <Button
                v-if="slotProps.data.nacex && slotProps.data.nacex.labelBase64"
                icon="pi pi-print"
                class="p-button-sm p-button-warning p-mr-1"
                @click="downloadLabel(slotProps.data)"
                v-tooltip.top="'Etiqueta'"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-sm p-button-danger"
                @click="confirmDelete(slotProps.data)"
                v-tooltip.top="'Eliminar'"
              />
            </template>
          </Column>
          <template #expansion="slotProps">
            <div v-if="slotProps.data.nacex">
              <div v-if="slotProps.data.loadingDetails" class="p-text-center p-p-3">
                <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem"></i>
                <p class="p-mt-2">A carregar detalhes NACEX...</p>
              </div>
              <div v-else-if="slotProps.data.nacexDetails" class="expanded-row-content">
                <div class="p-grid p-mb-2">
                  <div class="p-col-12 p-md-6">
                    <h5 class="text-primary p-mb-2">
                      <i class="pi pi-building p-mr-2"></i>Local de Recolha
                    </h5>
                    <div class="info-card" v-if="slotProps.data.nacexDetails.pickup_location">
                      <strong>{{ slotProps.data.nacexDetails.pickup_location.name }}</strong><br>
                      {{ slotProps.data.nacexDetails.pickup_location.address }}<br>
                      {{ slotProps.data.nacexDetails.pickup_location.zip }} {{ slotProps.data.nacexDetails.pickup_location.city }}<br>
                      <small v-if="slotProps.data.nacexDetails.pickup_location.phone">
                        <i class="pi pi-phone"></i> {{ slotProps.data.nacexDetails.pickup_location.phone }}
                      </small>
                    </div>
                    <div class="info-card" v-else-if="slotProps.data.nacexDetails.sender">
                      <strong>{{ slotProps.data.nacexDetails.sender.name }}</strong><br>
                      {{ slotProps.data.nacexDetails.sender.address }}<br>
                      {{ slotProps.data.nacexDetails.sender.zip }} {{ slotProps.data.nacexDetails.sender.city }}<br>
                      <small v-if="slotProps.data.nacexDetails.sender.phone">
                        <i class="pi pi-phone"></i> {{ slotProps.data.nacexDetails.sender.phone }}
                      </small>
                    </div>
                  </div>
                  <div class="p-col-12 p-md-6">
                    <h5 class="text-primary p-mb-2">
                      <i class="pi pi-info-circle p-mr-2"></i>Detalhes da Recolha
                    </h5>
                    <div class="info-card">
                      <strong>Nº NACEX:</strong> {{ slotProps.data.nacex.shipmentCode }}<br>
                      <strong>Estado:</strong> {{ slotProps.data.nacex.status }}<br>
                      <template v-if="slotProps.data.nacexDetails.pickup_number">
                        <strong>Nº Recolha:</strong> {{ slotProps.data.nacexDetails.pickup_number }}<br>
                      </template>
                      <template v-if="slotProps.data.nacexDetails.packages">
                        <strong>Volumes:</strong> {{ slotProps.data.nacexDetails.packages }}
                        <span v-if="slotProps.data.nacexDetails.weight"> ({{ slotProps.data.nacexDetails.weight }} kg)</span><br>
                      </template>
                      <template v-if="slotProps.data.nacexDetails.service">
                        <strong>Serviço:</strong> {{ slotProps.data.nacexDetails.service }}<br>
                      </template>
                    </div>
                  </div>
                </div>
                <div v-if="slotProps.data.nacexDetails.history && slotProps.data.nacexDetails.history.length > 0">
                  <h5 class="p-mb-2"><i class="pi pi-list p-mr-2"></i>Histórico de Estados</h5>
                  <DataTable :value="slotProps.data.nacexDetails.history" class="p-datatable-sm history-table">
                    <Column field="datetime" header="Data / Hora" />
                    <Column field="status" header="Estado">
                      <template #body="h">
                        <span style="font-weight: 600;">{{ h.data.status }}</span>
                      </template>
                    </Column>
                    <Column field="details" header="Detalhes" />
                    <Column field="agency" header="Agência" />
                  </DataTable>
                </div>
              </div>
              <div v-else class="p-text-center p-p-3" style="color: #ff9800;">
                <i class="pi pi-exclamation-circle p-mr-2"></i>Não foi possível carregar os detalhes NACEX.
              </div>
            </div>
            <div v-else class="expanded-row-content">
              <div class="p-mb-3" style="color: #6c757d; font-size: 0.95rem;">
                <i class="pi pi-info-circle p-mr-2"></i>Esta recolha não está ligada a uma operação NACEX.
              </div>
              <h5 class="text-primary p-mb-3">
                <i class="pi pi-directions p-mr-2"></i>Detalhes da Recolha
              </h5>
              <div class="p-grid p-formgrid p-mb-3">
                <div class="p-col-12 p-md-4">
                  <div class="info-card">
                    <strong>Transportadora:</strong> {{ slotProps.data.carrier || '—' }}<br>
                    <strong>Data da Recolha:</strong> {{ slotProps.data.pickupDate || '—' }}<br>
                    <strong>Marcada Por:</strong> {{ slotProps.data.scheduledByName || '—' }}<br>
                    <strong>Estado:</strong>
                    <span :class="slotProps.data.scheduled ? 'badge badge-success p-ml-1' : 'badge badge-warning p-ml-1'">
                      {{ slotProps.data.estado }}
                    </span>
                    <span v-if="slotProps.data.reschedule" class="badge badge-danger p-ml-1">Remarcar</span>
                  </div>
                </div>
                <div class="p-col-12 p-md-4">
                  <div class="info-card">
                    <strong>Guia Transporte:</strong> {{ slotProps.data.trackingNumber || '—' }}<br>
                    <strong>Local de Recolha:</strong> {{ slotProps.data.pickupAddress || '—' }}<br>
                    <strong>Local de Entrega:</strong> {{ slotProps.data.deliveryAddress || '—' }}
                  </div>
                </div>
                <div class="p-col-12 p-md-4">
                  <div class="info-card">
                    <strong>Observações:</strong> {{ slotProps.data.notes || '—' }}<br>
                    <strong>Motivos de Atraso:</strong> {{ slotProps.data.delayReasons || '—' }}
                  </div>
                </div>
              </div>

              <!-- Other Address Info (if OUTRA is selected) -->
              <div v-if="slotProps.data.pickupAddress === 'OUTRA' || slotProps.data.deliveryAddress === 'OUTRA'" class="p-mt-3">
                <h5 class="text-primary p-mb-2">
                  <i class="pi pi-home p-mr-2"></i>Outra Morada
                </h5>
                <div class="info-card" v-if="slotProps.data.otherAddress">
                  <strong>Nome:</strong> {{ slotProps.data.otherAddress.name || '—' }}<br>
                  <strong>Morada:</strong> {{ slotProps.data.otherAddress.street || '—' }}<br>
                  <strong>Localidade:</strong> {{ slotProps.data.otherAddress.city || '—' }}<br>
                  <strong>Código Postal:</strong> {{ slotProps.data.otherAddress.postalCode || '—' }}<br>
                  <strong>Telefone:</strong> {{ slotProps.data.otherAddress.phone || '—' }}
                </div>
              </div>
            </div>
          </template>
          <template #empty>
            <div class="p-text-center p-p-3">Nenhuma recolha registada.</div>
          </template>
        </DataTable>
      </template>
    </Card>

    <!-- Inline Form -->
    <Card v-if="showForm">
      <template #title>
        <div class="p-d-flex p-jc-between p-ai-center">
          <span>{{ editingId ? 'Editar Recolha' : 'Nova Recolha' }}</span>
          <Button
            icon="pi pi-arrow-left"
            label="Voltar"
            class="p-button-sm p-button-text"
            @click="closeForm"
          />
        </div>
      </template>
      <template #content>
        <div>
          <div class="p-fluid">
            <div class="p-grid p-formgrid">
              <div class="p-field p-col-12 p-md-3">
                <label>Transportadora *</label>
                <Dropdown
                  :disabled="editingId !== null"
                  v-model="form.carrier"
                  :options="transportadoras"
                  optionLabel="label"
                  optionValue="value"
                  class="p-inputtext-sm w-full"
                  appendTo="body"
                  placeholder="Selecione"
                />
              </div>

              <div class="p-field p-col-12 p-md-3" v-if="!isNacex">
                <label>Data de Recolha</label>
                <Calendar
                  v-model="form.pickupDate"
                  dateFormat="dd/mm/yy"
                  :showIcon="true"
                  :disabledDays="[0,6]"
                  inputClass="p-inputtext-sm"
                />
              </div>

              <div class="p-field p-col-12 p-md-3">
                <label>Marcada Por</label>
                <Dropdown
                  v-model="form.scheduledBy"
                  :options="technicians"
                  optionLabel="username"
                  optionValue="user_id"
                  class="p-inputtext-sm w-full"
                  appendTo="body"
                  placeholder="Selecione"
                />
              </div>

              <div class="p-field p-col-12 p-md-3 p-d-flex p-ai-end p-pb-3" v-if="!isNacex">
                <div class="p-d-flex p-ai-center p-mr-3">
                  <Checkbox v-model="form.scheduled" :binary="true" />
                  <label class="p-ml-2">Marcada?</label>
                </div>
                <div class="p-d-flex p-ai-center">
                  <Checkbox v-model="form.reschedule" :binary="true" />
                  <label class="p-ml-2" style="color: #ef4444;">Remarcar?</label>
                </div>
              </div>

              <div class="p-field p-col-12 p-md-4" v-if="!isNacex">
                <label>Guia Transporte</label>
                <InputText v-model="form.trackingNumber" class="p-inputtext-sm" />
              </div>

              <div class="p-field p-col-12 p-md-2" v-if="!isNacex">
                <label>Morada de Recolha</label>
                <Dropdown
                  v-model="form.pickupAddress"
                  :options="moradaOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="p-inputtext-sm w-full"
                  appendTo="body"
                />
              </div>

              <div class="p-field p-col-12 p-md-2" v-if="!isNacex">
                <label>Morada de Entrega</label>
                <Dropdown
                  v-model="form.deliveryAddress"
                  :options="moradaOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="p-inputtext-sm w-full"
                  appendTo="body"
                />
              </div>

              <div class="p-field p-col-12 p-md-5" v-if="!isNacex">
                <fieldset class="outra-morada-fieldset">
                  <legend>Outra Morada</legend>
                  <div class="p-grid p-formgrid">
                    <div class="p-field p-col-12 p-md-6">
                      <label>Nome</label>
                      <InputText v-model="form.otherAddress.name" class="p-inputtext-sm w-full" />
                    </div>
                    <div class="p-field p-col-12 p-md-6">
                      <label>Morada</label>
                      <InputText v-model="form.otherAddress.street" class="p-inputtext-sm w-full" />
                    </div>
                    <div class="p-field p-col-12 p-md-4">
                      <label>Localidade</label>
                      <InputText v-model="form.otherAddress.city" class="p-inputtext-sm w-full" />
                    </div>
                    <div class="p-field p-col-12 p-md-4">
                      <label>Cod Postal</label>
                      <InputText v-model="form.otherAddress.postalCode" class="p-inputtext-sm w-full" />
                    </div>
                    <div class="p-field p-col-12 p-md-4">
                      <label>Telefone</label>
                      <InputText v-model="form.otherAddress.phone" class="p-inputtext-sm w-full" />
                    </div>
                  </div>
                </fieldset>
              </div>

              <div class="p-field p-col-12 p-md-12 p-d-flex p-ai-end p-pb-3" v-if="!isNacex">
                <div class="p-field p-col-12 p-md-6">
                  <label>Observações</label>
                  <Textarea v-model="form.notes" rows="3" class="p-inputtext-sm w-full" />
                </div>

                <div class="p-field p-col-12 p-md-6">
                  <label>Motivos de Atraso</label>
                  <Textarea v-model="form.delayReasons" rows="3" class="p-inputtext-sm w-full" />
                </div>
              </div>
            </div>

            <!-- NACEX Inline Fields -->
            <NacexInlineFields
              v-if="isNacex && !editingId"
              ref="nacexFields"
              v-model="nacexData"
              type="pickup"
              :patData="patData"
            />
            <div v-if="isNacex && editingId" class="p-p-3 p-text-center" style="color: #6c757d;">
              <i class="pi pi-info-circle p-mr-2"></i>
              Campos NACEX não editáveis após criação.
            </div>

          </div>
          <div class="p-d-flex p-jc-end p-mt-4 button-row">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              class="p-button-text p-mr-2"
              @click="closeForm"
            />
            <Button
              :label="editingId ? 'Atualizar' : 'Registar Recolha'"
              icon="pi pi-check"
              class="p-button-success"
              :loading="saving"
              @click="savePickup"
            />
          </div>
        </div>
      </template>
    </Card>

    <!-- Label Dialog (PDF result — stays as dialog) -->
    <Dialog
      header="Etiqueta NACEX"
      :visible.sync="showLabelDialog"
      :style="{ width: '50vw' }"
      :modal="true"
    >
      <div class="p-text-center" style="text-align: center;">
        <p class="p-mb-3" style="font-size: 1.1rem;">
          <i class="pi pi-check-circle" style="color: green; font-size: 1.5rem;"></i>
          Recolha NACEX criada com sucesso!
        </p>
        <div v-if="labelPdfData">
          <Button label="Imprimir Etiqueta" icon="pi pi-print" @click="printLabel" class="p-mb-3" />
          <br />
          <iframe :src="labelPdfData" width="100%" height="450px" style="border: 1px solid #ccc;"></iframe>
        </div>
        <div v-else>
          <p>A recolha foi agendada. O estafeta levará a etiqueta.</p>
        </div>
      </div>
      <template #footer>
        <Button label="Fechar" icon="pi pi-times" class="p-button-secondary" @click="showLabelDialog = false" />
      </template>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import PatService from "../../services/pat.service";
import ShipmentsService from "../../services/shipments.service";
import { TRANSPORTADORAS } from "../../constants/pat-options";
import NacexInlineFields from "../shipment/NacexInlineFields.vue";

export default {
  name: "PatPickup",
  components: {
    Loading,
    NacexInlineFields,
  },
  props: {
    patId: { type: Number, required: true },
    patInfo: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
      saving: false,
      pickups: [],
      technicians: [],
      transportadoras: TRANSPORTADORAS,
      moradaOptions: [
        { label: "LOJA", value: "LOJA" },
        { label: "CLIENTE", value: "CLIENTE" },
        { label: "OUTRA", value: "OUTRA" },
      ],
      showForm: false,
      showLabelDialog: false,
      editingId: null,
      expandedRows: [],
      labelPdfData: null,
      nacexData: {},
      form: this.getEmptyForm(),
    };
  },
  computed: {
    canEdit() {
      return this.can("managePATs");
    },
    isNacex() {
      return this.form.carrier === "NACEX";
    },
    patData() {
      return this.buildPatData();
    },
  },
  async mounted() {
    this.loading = true;
    await this.loadData();
    this.loading = false;
  },
  methods: {
    getStatusClass(status) {
      const normalized = (status || '').toString().trim().toUpperCase();

      if (['CREATED', 'SCHEDULED', 'PENDIENTE', 'NOTIFICADO', 'RECOGIDO'].includes(normalized)) {
          return 'info';
      }

      if ([
          'EN TRANSITO',
          'TRANSITO',
          'EN REPARTO',
          'REPARTO',
          'ASIGNADA A MENSAJERO PARA RECOGER',
          'DISP.P.E.'
      ].includes(normalized)) {
          return 'warning';
      }

      if (['ENTREGADO', 'OK', 'RECOGIDA'].includes(normalized)) {
          return 'success';
      }

      if ([
          'CANCELED',
          'ANULADO',
          'ANULADA',
          'INCIDENCIA',
          'SOL SIN OK',
          'RECHAZADA'
      ].includes(normalized)) {
          return 'danger';
      }
      return 'secondary';
    },
    getEmptyForm() {
      let currentUser = this.$store.state.auth.user;
      return {
        pickupDate: this.getNextBusinessDay(1),
        scheduledBy: currentUser ? currentUser.id : null,
        scheduled: false,
        reschedule: false,
        carrier: 'NACEX',
        trackingNumber: "",
        pickupAddress: "CLIENTE",
        deliveryAddress: "LOJA",
        otherAddress: { name: "", street: "", city: "", postalCode: "", phone: "" },
        notes: "",
        delayReasons: "",
      };
    },
    getNextBusinessDay(offsetDays) {
        const date = new Date();
        date.setDate(date.getDate() + offsetDays);
        while (date.getDay() === 0 || date.getDay() === 6) {
            date.setDate(date.getDate() + 1);
        }
        return date;
    },
    async loadData() {
      try {
        const [pickupsRes, techs] = await Promise.all([
          PatService.getPickups(this.patId),
          PatService.getTechnicians(),
        ]);
        this.pickups = pickupsRes?.data || pickupsRes || [];
        if (techs) this.technicians = techs;
      } catch (err) {
        this.$toast.add({ severity: "error", summary: "Erro", detail: "Erro ao carregar recolhas.", life: 3000 });
      }
    },
    openNewForm() {
      this.editingId = null;
      this.form = this.getEmptyForm();
      this.nacexData = {};
      this.showForm = true;
    },
    openEditForm(row) {
      this.editingId = row.id;
      this.form = {
        pickupDate: row.pickupDate ? new Date(row.pickupDate.split('-').reverse().join('-')) : null,
        scheduledBy: row.scheduledBy,
        scheduled: row.scheduled || false,
        reschedule: row.reschedule || false,
        carrier: row.carrier || 'Outro',
        trackingNumber: row.trackingNumber || "",
        pickupAddress: row.pickupAddress || "CLIENTE",
        deliveryAddress: row.deliveryAddress || "LOJA",
        otherAddress: {
          name: row.otherAddress?.name || "",
          street: row.otherAddress?.street || "",
          city: row.otherAddress?.city || "",
          postalCode: row.otherAddress?.postalCode || "",
          phone: row.otherAddress?.phone || "",
        },
        notes: row.notes || "",
        delayReasons: row.delayReasons || "",
      };
      this.showForm = true;
    },
    closeForm() {
      this.showForm = false;
      this.editingId = null;
      this.form = this.getEmptyForm();
      this.nacexData = {};
    },
    buildPayload() {
      const payload = {
        pickup_date: this.form.pickupDate ? this.formatDate(this.form.pickupDate) : null,
        scheduled_by: this.form.scheduledBy,
        scheduled: this.form.scheduled,
        reschedule: this.form.reschedule,
        carrier: this.form.carrier,
        tracking_number: this.form.trackingNumber,
        pickup_address: this.form.pickupAddress,
        delivery_address: this.form.deliveryAddress,
        other_address_name: this.form.otherAddress.name,
        other_address_street: this.form.otherAddress.street,
        other_address_city: this.form.otherAddress.city,
        other_address_postal_code: this.form.otherAddress.postalCode,
        other_address_phone: this.form.otherAddress.phone,
        notes: this.form.notes,
        delay_reasons: this.form.delayReasons,
      };

      if (this.isNacex && !this.editingId && this.$refs.nacexFields) {
        Object.assign(payload, this.$refs.nacexFields.getNacexPayload());
      }

      return payload;
    },
    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    async savePickup() {
      this.saving = true;
      try {
        const isEdit = !!this.editingId;
        let result;
        if (isEdit) {
          result = await PatService.updatePickup(this.patId, this.editingId, this.buildPayload());
        } else {
          result = await PatService.createPickup(this.patId, this.buildPayload());
        }

        if (!isEdit && this.isNacex && result?.data?.nacex?.labelBase64) {
          this.labelPdfData = `data:application/pdf;base64,${result.data.nacex.labelBase64}`;
          this.showLabelDialog = true;
        }

        this.closeForm();
        await this.loadData();
        this.$toast.add({
          severity: "success",
          summary: "Sucesso",
          detail: isEdit ? "Recolha atualizada." : "Recolha criada com sucesso.",
          life: 3000,
        });
      } catch (err) {
        const msg = err.response?.data?.details || err.response?.data?.error || "Erro ao guardar recolha.";
        this.$toast.add({ severity: "error", summary: "Erro", detail: msg, life: 5000 });
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(row) {
      this.$confirm.require({
        message: row.nacex
          ? "Esta recolha está ligada a uma operação NACEX e será cancelada. Tem a certeza?"
          : "Tem a certeza que pretende eliminar esta recolha?",
        header: "Confirmar eliminação",
        icon: "pi pi-exclamation-triangle",
        acceptClass: "p-button-danger",
        acceptLabel: "Eliminar",
        rejectLabel: "Cancelar",
        accept: () => this.deletePickupRow(row),
      });
    },
    async deletePickupRow(row) {
      this.loading = true;
      try {
        await PatService.deletePickup(this.patId, row.id);
        await this.loadData();
        this.$toast.add({ severity: "success", summary: "Sucesso", detail: "Recolha eliminada.", life: 3000 });
      } catch (err) {
        this.$toast.add({ severity: "error", summary: "Erro", detail: "Erro ao eliminar recolha.", life: 3000 });
      } finally {
        this.loading = false;
      }
    },
    async onRowExpand(event) {
      const row = event.data;
      if (!row.nacex) return;
      this.$set(row, 'loadingDetails', true);
      this.$set(row, 'nacexDetails', null);
      this.expandedRows = [row];
      try {
        const response = await ShipmentsService.getShipmentById(row.nacex.id);
        if (response && response.data && response.data.data && response.data.data.nacex_details) {
          this.$set(row, 'nacexDetails', response.data.data.nacex_details);
        } else if (response && response.data && response.data.nacex_details) {
          this.$set(row, 'nacexDetails', response.data.nacex_details);
        }
      } catch (err) {
        this.$toast.add({ severity: "error", summary: "Erro", detail: "Não foi possível carregar detalhes NACEX.", life: 3000 });
      } finally {
        this.$set(row, 'loadingDetails', false);
      }
    },
    onRowCollapse(event) {
      this.expandedRows = this.expandedRows.filter((r) => r.id !== event.data.id);
    },
    openTracking(url) {
      if (url) window.open(url, '_blank');
    },
    async syncNacexStatus(row) {
      try {
        const result = await PatService.syncPickupStatus(this.patId, row.id);
        await this.loadData();
        this.$toast.add({
          severity: "success",
          summary: "Sincronizado",
          detail: `Estado: ${result.status}`,
          life: 3000,
        });
      } catch (err) {
        this.$toast.add({ severity: "error", summary: "Erro", detail: "Erro ao sincronizar.", life: 3000 });
      }
    },
    downloadLabel(row) {
      if (!row.nacex?.labelBase64) return;
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${row.nacex.labelBase64}`;
      link.download = `Nacex_Recolha_${row.nacex.shipmentCode || row.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    printLabel() {
      if (!this.labelPdfData) return;
      const link = document.createElement("a");
      link.href = this.labelPdfData;
      link.download = "Nacex_Recolha.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    estadoBadgeClass(row) {
      if (row.nacex) {
        const s = row.nacex.status;
        if (s === 'DELIVERED' || s === 'COMPLETED') return 'badge badge-success';
        if (s === 'CANCELED') return 'badge badge-danger';
        if (s === 'SCHEDULED' || s === 'CREATED') return 'badge badge-info';
        return 'badge badge-warning';
      }
      return row.scheduled ? 'badge badge-success' : 'badge badge-secondary';
    },
    buildPatData() {
      if (!this.patInfo) return {};
      const data = {
        reference: String(this.patId),
        client: null,
        entity: null,
        externalRef: this.patInfo.externalRef || ''
      };

      if (this.patInfo.client) {
        data.client = this.parseContactData(this.patInfo.client);
      }

      if (this.patInfo.entity) {
        data.entity = this.parseContactData(this.patInfo.entity);
      }

      return data;
    },
    parseContactData(contact) {
      if (!contact) return null;
      const parsed = {
        name: contact.name || contact.nome || '',
        address: contact.address || contact.morada || '',
        zip: '',
        city: contact.city || contact.local || '',
        phone: contact.mobilPhone || contact.telephone || contact.tlmvl || '',
        email: contact.email || '',
        country: 'PT',
        observations1: '',
        observations2: ''
      };

      const codpost = contact.zip_code || contact.zipCode || contact.postalCode || '';
      if (codpost) {
        const parts = codpost.split(' ');
        parsed.zip = parts[0] || '';
        parsed.city = parts.slice(1).join(' ') || contact.city || contact.local || '';
      }

      return parsed;
    },
  },
};
</script>

<style scoped>
.outra-morada-fieldset {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.5rem 1rem 1rem;
}

.outra-morada-fieldset legend {
  padding: 0 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
}

.gap-2 {
  gap: 0.5rem;
}

.w-full {
  width: 100%;
}

.nacex-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #2196F3;
  color: white;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}
.badge-success { background: #c8e6c9; color: #256029; }
.badge-danger { background: #ffcdd2; color: #c63737; }
.badge-info { background: #b3e5fc; color: #23547b; }
.badge-warning { background: #ffd8b2; color: #805b36; }
.badge-secondary { background: #e0e0e0; color: #616161; }

.button-row {
    border-top: 1px solid #dee2e6;
    padding-top: 1.5rem;
    margin-top: 1rem;
}

.expanded-row-content {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin: 0.5rem 0;
  padding: 1rem;
}

.text-primary {
  color: #0056b3;
  font-size: 1rem;
  font-weight: 600;
}

.info-card {
  background: white;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  font-size: 0.9rem;
  line-height: 1.6;
}

.history-table {
  border: 1px solid #dee2e6;
}
</style>
